/**
 * Shared field activity-note request builder (card 0cEzrRKvCW1D).
 *
 * Field completion/standalone notes now attach the SAME way every other
 * activity note does — via POST /api/v1/workflows/notes/create-with-activity,
 * which inserts a `notes` row AND an `activities_notes_junction` link. That is
 * exactly what the web app's activity Notes tab reads
 * (client/components/Activities/ActivityNotes.tsx via
 * useActivitiesNotesJunctionList), so a note written here shows up everywhere
 * activity notes show. We NO LONGER write the note text to
 * activities.verification_notes from the field path — notes have one home.
 *
 * organization_id is resolved from /auth/me here (mirroring photoUpload.ts) so
 * neither the direct call site nor the offline-queue payload has to plumb it.
 *
 * The payload is byte-free JSON (plain text), so there is no iOS IndexedDB
 * blob-loss concern like the photo path has — it queues/replays as-is.
 */

import { httpClient } from '../../api/http-client.js'

/** Everything needed to create one field activity note. */
export interface FieldNoteRequest {
  /** The activity the note is linked to. */
  activityId: string
  /** Note body (required). */
  content: string
  /** Note category — defaults server-side to 'general' when omitted. */
  noteType?: string
}

/** Outcome of a direct (online) note create attempt. */
export type CreateNoteResult =
  | { ok: true }
  | { ok: false; retryable: boolean; error: string }

async function getOrganizationId(): Promise<string | null> {
  // /api/v1/auth/me returns the current user's profile; the create-with-activity
  // endpoint requires organization_id. Resolve it here so call sites/queue
  // payloads stay lean — same pattern as photoUpload.ts / registerHandlers.ts.
  try {
    const response = await httpClient.get<{
      data?: { organizationId?: string }
      user?: { organizationId?: string }
    }>('/auth/me')
    return (
      (response as any)?.data?.organizationId
      ?? (response as any)?.user?.organizationId
      ?? (response as any)?.organizationId
      ?? null
    )
  } catch {
    return null
  }
}

/**
 * POST a field activity note to create-with-activity (used online and at
 * offline replay). Resolves organization_id from /auth/me, then posts
 * { content, note_type, organization_id, activity_id }.
 *
 * On a network error / missing org / 5xx, returns retryable:true so the caller
 * (or the offline handler) can retry later instead of losing the note. A 4xx is
 * non-retryable.
 */
export async function createActivityNote(req: FieldNoteRequest): Promise<CreateNoteResult> {
  const organizationId = await getOrganizationId()
  if (!organizationId) {
    return { ok: false, retryable: true, error: 'Could not resolve organization for note (auth)' }
  }

  try {
    const response = await fetch('/api/v1/workflows/notes/create-with-activity', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: req.content,
        note_type: req.noteType || 'general',
        organization_id: organizationId,
        activity_id: req.activityId,
      }),
    })
    if (!response.ok) {
      const text = await response.text().catch(() => response.statusText)
      const message = `HTTP ${response.status}: ${text}`
      if (response.status >= 400 && response.status < 500) {
        return { ok: false, retryable: false, error: message }
      }
      return { ok: false, retryable: true, error: message }
    }
    return { ok: true }
  } catch (err) {
    // Network failure (offline / dropped connection) — retryable → fall back to queue.
    return { ok: false, retryable: true, error: err instanceof Error ? err.message : String(err) }
  }
}
